import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';
import axios from 'axios';

export interface GetScanResponse {
  id?: number;
  member_id?: string;
  booking_id?: string;
  scan_type?: string;
  show_in_app?: boolean;
  access_token?: string;
  access_key?: string;
  created_at?: string;
  updated_at?: string;
  status?: string;
  [key: string]: any;
}

export interface ScanCenter {
  id: number;
  name: string;
  street: string;
  latitude: string;
  longitude: string;
  street_number: string | null;
  postcode: string;
  description: string | null;
  open_time_description: string;
  opening_hours: Array<{
    day: string;
    open: string | null;
    close: string | null;
  }>;
  headquarter_name: string;
  region_id: number;
  city_id: number;
  status: string | null;
  website: string;
  site_type: string;
  strength: string;
  earliest_available_date: string;
  final_price: string;
  asap_price: string;
  slug: string;
  distance: number;
  address_without_name: string;
  avg_rating: string | null;
  is_rating_display_disabled: boolean;
  is_review_display_disabled: boolean;
  nextDayScans: boolean;
  bookingWaitDuration: number | null;
  partner: boolean;
  image: string;
  images?: string[];
  phone_number?: string;
  recommended: boolean;
  facilities: string[];
  supported_scan_types: string[];
}

export interface SearchScanCentersParams {
  location?: string;
}

export interface SearchScanCentersResponse {
  data: ScanCenter[];
  errors: Record<string, any>;
}

export interface CreateScanRequest {
  member_id: string;
  booking_id: string;
  scan_type: 'full_body_mri';
  show_in_app?: boolean;
  skip_dob?: boolean;
}

export interface UpdateScanRequest {
  member_id: string;
  booking_id: string;
  scan_type: 'full_body_mri';
  show_in_app?: boolean;
  skip_dob?: boolean;
}

export const getScan = async (memberId: string): Promise<GetScanResponse | null> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}scans/get/`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response?.status === 404 || error.response?.data?.detail?.includes('not found')) {
      return null;
    }
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const createScan = async (request: CreateScanRequest): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}scans/`,
      request
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const updateScan = async (request: UpdateScanRequest): Promise<any> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}scans/`,
      request
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const searchScanCenters = async (
  params: SearchScanCentersParams
): Promise<SearchScanCentersResponse> => {
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  // Only location is conditional based on user selection
  if (params.location) {
    queryParams.append('location', params.location);
  }
  
  // Hardcoded parameters - always included
  queryParams.append('body_parts[]', 'Full Body');
  queryParams.append('scan_type', 'mri');
  queryParams.append('only_fullbody', 'true');

  // Direct call to getscanned.me API
  try {
    const baseURL = process.env.REACT_APP_SCAN_SEARCH_API_URL || 'https://ukstaging.getscanned.me';
    const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    
    const { data } = await axios.get<SearchScanCentersResponse>(
      `${cleanBaseURL}/api/v1/search?${queryParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    return data;
  } catch (error: any) {
    // If direct call fails due to CORS, provide helpful error message
    if (error.code === 'ERR_NETWORK' || 
        error.message?.includes('CORS') || 
        error.message?.includes('Network Error') ||
        (error.response === undefined && error.request)) {
      throw new Error(
        'CORS Error: Unable to connect to scan center search service. ' +
        'Please configure a backend proxy endpoint at /api/v1/scans/search/ ' +
        'that forwards requests to the getscanned.me API.'
      );
    }
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw error;
  }
};

